import { filter, fromEvent, map, merge } from "rxjs";
import { store, store$ } from '@redux/store';
import { segmentPageView, segmentTrack, segmentIdentify } from "features/segment";
import typeahead from "features/articleSearchBox";
import { selectModal, selectFetchingModal } from 'layout/redux/selectors';
import HousingProductType from "constants/housingProductType";

const getPopularPillElement = (event) => event.target.closest('[data-navigator-item]');
const getStandardCardElement = (event) => event.target.closest("[data-card]");
const isMetroSpecificSection = (event) => !!event.target.closest('[data-section-name="MetroSpecificSubjects"]');
const isMostPopularSection = (event) => !!event.target.closest('[data-section-name="MostPopularArticles"]');
const isCategorySection = (event) => !!event.target.closest('[data-section-name="categoryArticles"]');
const isCommunityStandardCard = (dataset) => dataset.type === HousingProductType.Community().getName();
const isPlanStandardCard = (dataset) => dataset.type === HousingProductType.Plan().getName();
const isSpecStandardCard = (dataset) => dataset.type === HousingProductType.Spec().getName();
const getLinkableArticleElement = (event) => event.target.closest("[data-article-card-link]");
const isListingForYouSection = (event) => !!event.target.closest("[data-listings-for-you-container]");
const isLinkableListingElement = (event) => event.target instanceof HTMLAnchorElement;
const isFavoriteIcon = (event) => !!event.target.closest(".icon__save");
const isCallToAction = (event) => !!event.target.closest("[data-lead-generator-opener]");
const isStandardCard = (event) => !!event.target.closest("[data-standard-card]");
const breadcrumbItems = Array.from(document.querySelectorAll('[data-breadcrumb-item]'));

/* Observable that listens for click events on the document.body */
const bodyClick$ = fromEvent(document.body, "click");
const listingForYouSectionClick$ = bodyClick$.pipe(
  filter((event) => isListingForYouSection(event))
);
const onWindowLoad$ = fromEvent(window, "load");

/**
 * triggerPaidSpecModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidSpecModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    button_action: modalInformation.buttonAction,
    city: listingInformation.city,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    home_price: listingInformation.price,
    home_type: listingInformation.homeStatus,
    market_id: listingInformation.marketId,
    market_name: listingInformation.marketName,
    primary_image_url: listingInformation.imageUrl,
    state: listingInformation.stateAbbreviation,
    spec_id: listingInformation.specId,
    zip: listingInformation.zip,
  };

  return customProperties;
}

/**
 * triggerPaidCommunityModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidCommunityModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    market_id: listingInformation.marketId,
    market_name: listingInformation.marketName,
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    community_status: listingInformation.marketingStatusLabel,
    primary_image_url: listingInformation.imageUrl,
    community_low_price: listingInformation.priceLow,
    community_high_price: listingInformation.priceHigh,
    city: listingInformation.city,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
    button_action: modalInformation.buttonAction,
  };

  return customProperties;
}

/**
 * triggerPaidPlanModalSubmitted: Triggers the event when the modal form is submitted.
 * This event only applies for the following forms:
 * - Free Brochure
 * - Get More Information
 * @param {object} modalInformation The information related to the modal opened.
 */
function triggerPaidPlanModalSubmitted(modalInformation) {
  const { listingInformation } = modalInformation;
  const customProperties = {
    builder_id: listingInformation.builderId,
    builder_name: listingInformation.brandName,
    button_action: modalInformation.buttonAction,
    city: listingInformation.city,
    community_id: listingInformation.id,
    community_name: listingInformation.communityName,
    home_price: listingInformation.price,
    home_type: listingInformation.homeStatus,
    market_id: listingInformation.marketId,
    market_name: listingInformation.marketName,
    plan_id: listingInformation.planId,
    primary_image_url: listingInformation.imageUrl,
    state: listingInformation.stateAbbreviation,
    zip: listingInformation.zip,
  };

  return customProperties;
}

/* An observable stream that listens for click events. It then maps each click event to the closest
ancestor element of the clicked target that has a `data-breadcrumb-item` attribute.
The `filter(Boolean)` function is used to filter out any falsy values. */
const onBreadcrumbClick$ = bodyClick$.pipe(
  map((event) => event.target?.closest('[data-breadcrumb-item]')),
  filter(Boolean)
);

/**
 * Dispatches a Google Analytics 4 event when a breadcrumb link is clicked.
 * @param breadcrumbLink - is the link element that represents a
 * breadcrumb item in a website's navigation.
 */
const triggerBreadcrumbClickEvent = (breadcrumbLink) => {
  const breadcrumbIndex = breadcrumbItems.findIndex((item) => item === breadcrumbLink);
  const customProperties = {
    breadcrumb_position: breadcrumbIndex + 1,
    destination_url: breadcrumbLink.href,
    link_text: breadcrumbLink.innerText,
  };

  store.dispatch(segmentTrack({ eventName: 'Breadcrumb_clicked', customProperties }));
};

onBreadcrumbClick$.subscribe(triggerBreadcrumbClickEvent);

/**
 * triggerPageViewEvent: Dispatch the page view on window load.
 */
const triggerPageViewEvent = function () {
  const customProperties = {
    section: "Learning Center",
  };
  store.dispatch(segmentPageView({ customProperties }));
};

onWindowLoad$.subscribe(triggerPageViewEvent);

/**
 * The getCardImageUrls function takes a cardElement as an argument and returns an object with the following properties:
 * primaryImage, secondaryImage, terciaryImage.
 * if there are no images in the cardElement then each property will be assigned to an empty string.
 * This can be used to other events in the list view section.
 * @param cardElement Get the image elements from the card element
 *
 * @return An object with primaryImage, secondaryImage, terciaryImage.
 */
const getCardImageUrls = (cardElement) => {
  const imageElements = cardElement.querySelectorAll('img');

  if (imageElements) {
    return {
      primaryImage: imageElements[0]?.src || '',
      secondaryImage: imageElements[1]?.src || '',
      terciaryImage: imageElements[2]?.src || '',
    };
  }

  return {};
};

/**
* Retrieves custom properties Paid Plan cards
* related to a lead button click on a paid plan within a card element.
* @param cardData - The `cardData` parameter in the
* `getLeadButtonClickedCallToActionPaidPlanCustomProperties` function is expected to be an HTML
* element that represents a card containing data attributes related to a property listing. The
* function extracts specific data attributes from this element to create custom properties for
* tracking user interactions with
* @returns Returns an object  * containing custom properties related to a lead button click action
* on a paid plan. The custom properties include information to be logged
*/
const getLeadButtonClickedCallToActionPaidPlanCustomProperties = function (cardData){

  const leadButton = cardData.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || [];
  const cardDataset = cardData.dataset;

  const primaryImageUrl = cardDataset.imageUrl !== ''
    ? cardDataset.imageUrl
    : getCardImageUrls(cardData).primaryImage;

  const customProperties = {
      pageType: 'Learning Center',
      market_id: cardDataset.marketId,
      market_name: cardDataset.marketName,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.communityId,
      community_name: cardDataset.communityName,
      primary_image_url: primaryImageUrl,
      button_action: leadButtonDataset.buttonAction,
      plan_id: cardDataset.planId,
      home_type: cardDataset.homeStatus,
      home_price: cardDataset.price,
    };

    return customProperties;
}

/**
* Retrieves custom properties Paid Spec cards
* related to a lead button click on a card element.
* @param cardData - HTML element that represents a card
* containing various data attributes
* @returns Returns an object containing custom properties related to a lead button click
* action on a card element. The custom properties include information to be used for Segment events.
*/
const getLeadButtonClickedCallToActionPaidSpecCustomProperties = function (cardData){

  const leadButton = cardData.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || [];
  const cardDataset = cardData.dataset;

  const primaryImageUrl = cardDataset.imageUrl !== ''
    ? cardDataset.imageUrl
    : getCardImageUrls(cardData).primaryImage;

  const customProperties = {
      pageType: 'Learning Center',
      market_id: cardDataset.marketId,
      market_name: cardDataset.marketName,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.communityId,
      community_name: cardDataset.communityName,
      primary_image_url: primaryImageUrl,
      button_action: leadButtonDataset.buttonAction,
      spec_id: cardDataset.spec_id,
      home_type: cardDataset.homeStatus,
      home_price: cardDataset.price,
    };

    return customProperties;
}

/**
* Retrieves custom properties for Paid Community card
* related to a lead button click on a card element.
* @param cardData - HTML element that represents a card
* containing various data attributes
* @returns Returns an object containing custom properties related to a lead button click
* action on a card element. The custom properties include information to be used for Segment events.
*/
const getLeadButtonClickedPaidCommunityCustomProperties = function (cardData){

  const leadButton = cardData.querySelector('[data-lead-generator-opener]');
  const leadButtonDataset = leadButton.dataset || [];
  const cardDataset = cardData.dataset;

  const primaryImageUrl = cardDataset.imageUrl !== ''
    ? cardDataset.imageUrl
    : getCardImageUrls(cardData).primaryImage;

  const customProperties = {
      pageType: 'Learning Center',
      market_id: cardDataset.marketId,
      market_name: cardDataset.marketName,
      city: cardDataset.city,
      state: cardDataset.stateAbbreviation,
      zip: cardDataset.zip,
      builder_id: cardDataset.builderId,
      builder_name: cardDataset.brandName,
      community_id: cardDataset.communityId,
      community_name: cardDataset.communityName,
      primary_image_url: primaryImageUrl,
      button_action: leadButtonDataset.buttonAction,
      home_type: cardDataset.homeStatus,
      home_price: cardDataset.price,
    };

    return customProperties;
}

/**
 * Dispatches a Segment event when an article is clicked.
 * @param article - is the article that represents the element clicked.
 */
const triggerRecomendedArticlesClick = (article) => {
  const customProperties = {
    content_id: article.dataset.slug,
    content_title: article.dataset.title,
    recommendation_type: "similar articles",
  };

  store.dispatch(
    segmentTrack({ eventName: "Recommendation_Clicked", customProperties })
  );
};

/* Observable stream that listens for click events on the document body.
It then filters those events to only include ones where the clicked
element is identified as being in the most popular section (determined by the `isMostPopularSection`
function). */
const onMostPopularArticleClick$ = bodyClick$.pipe(
  filter(isMostPopularSection),
  map(getLinkableArticleElement),
  filter(Boolean)
);

onMostPopularArticleClick$.subscribe(triggerRecomendedArticlesClick);

/* Observable stream that listens for click events on the `document.body`.
 It then filters those events to only include ones where the clicked
 element is identified as being in the category articles section (determined by the
 `isCategorySection` function). */
const onCategoryArticlesClick$ = bodyClick$.pipe(
  filter(isCategorySection),
  map(getLinkableArticleElement),
  filter(Boolean)
);

onCategoryArticlesClick$.subscribe(triggerRecomendedArticlesClick);

/**
 * Dispatches a Analytics Event event when a popular pill is clicked.
 * @param pill - is the pill clicked in popular pill section.
 */
const triggerPopularPillClick = function(pill) {
  const customProperties = {
    page_type: 'Learning_Center',
    tag_text: pill.dataset.pillText,
    destination_url: pill.dataset.pillUrl,
  };

  store.dispatch(segmentTrack({ eventName: 'Tag_Clicked', customProperties }));
}

/**
 * Dispatches a Segment event when a search is done.
 * @param suggestion - is the suggestion element that represents the element clicked
 * in the typeahead.
 */
const triggerSearchExecutedEvent = (suggestion) => {
  const customProperties = {
    search_term: suggestion.dataset.title,
    search_location: "Learning Center",
  };

  store.dispatch(
    segmentTrack({ eventName: "Search_Executed", customProperties })
  );
};

typeahead
  .getSuggestionListClickedObserver()
  .subscribe(triggerSearchExecutedEvent);

/**
 * Dispatches a Segment Event When the submitt button is clicked.
 * @param customProperties - is the custom properties of the event.
 */
const triggerModalSubmittedEvent = function(customProperties) {
  store.dispatch(segmentTrack({ eventName: 'Lead Modal Form Submitted', customProperties }));
}

const triggerIdentifyModalSubmission = () => {
  store.dispatch(segmentIdentify({ eventName: 'Lead Modal Form Submitted' }));
};

/**
 * Dispatches a Segment event when metro specific article is clicked.
 * @param article - is the article metro specific article articles section.
 */
const triggerArticlesClick = (article) => {
  const customProperties = {
    article_id: article.dataset.slug,
    article_title: article.dataset.title,
  };

  store.dispatch(
    segmentTrack({ eventName: "Article_Clicked", customProperties })
  );
};

/**
 * Dispatches a Segment event when listing card is clicked.
 * @param data - is the data card on the listings section.
 */
const triggerListingClick = (data) => {
  const customProperties = {
    listing_id: data.card.dataset.id,
    listing_name: data.card.dataset.name,
    interaction_type: data.interactionType,
  };

  store.dispatch(
    segmentTrack({ eventName: "Listing_Interacted", customProperties })
  );
};

// Observers to popular pill section Events Logic
const onPopularPillClick$ = bodyClick$.pipe(
  map(getPopularPillElement),
  filter(Boolean)
);

onPopularPillClick$.subscribe(triggerPopularPillClick);

// Metro Specific Section Observers

/* Observable stream `onMetroSpecificArticlesClick$` that listens
  for click events on the `document.body`. Here's a breakdown of what each part of the code is
  doing: */
const onMetroSpecificArticlesClick$ = bodyClick$.pipe(
  filter(isMetroSpecificSection),
  map(getLinkableArticleElement),
  filter(Boolean)
);

// Listing For You Section Observers

/**
 * Dispatches a tracking event when the lead button is
 * clicked.
 * @param customProperties - The `customProperties` parameter in the `triggerLeadButtonClickedEvent`
 * function is likely intended to be an object that can hold additional properties or data related to
 * the event. These properties could include information such as the user's ID, timestamp, or any other
 * relevant details that you may want to track
 */
const triggerLeadButtonClickedEvent = function (customProperties){
  store.dispatch(segmentTrack({ eventName: 'Lead Button Clicked', customProperties }));
}

/* Observable stream `callToActionStandardCardClick$` that listens for
click events in the `listingForYouSectionClick$` stream. It filters out events that are considered
call-to-action events, then maps the event to the corresponding standard card element. It then
filters out any falsy values before emitting the final result. */
const callToActionStandardCardClick$ = listingForYouSectionClick$.pipe(
  filter(isCallToAction),
  map(getStandardCardElement),
  filter(Boolean),
);

/* Observable stream `callToActionCommunityCardClick$` that listens for
click events in the `listingForYouSectionClick$` stream. It filters the events to only include those
that are both a call to action and a community standard card. It then maps the event to get the
standard card element, filters out any falsy values, and finally maps the event to get custom
properties for a lead button clicked in a paid community. */
const callToActionCommunityCardClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isCommunityStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedPaidCommunityCustomProperties)
);

/* Observable stream `callToActionSpecCardClick$` that listens for click
events in the `listingForYouSectionClick$` stream. It filters the events to only include those that
are both a call to action and a standard card, then maps the event to extract the standard card
element. It then filters out any falsy values, maps the event to extract custom properties related
to a lead button click on a paid spec, and returns the final observable stream
`callToActionSpecCardClick$`. */
const callToActionSpecCardClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isSpecStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedCallToActionPaidSpecCustomProperties)
);

/* Observable stream `callToActionPlanCardClick$` that listens for click
events in the `listingForYouSectionClick$` stream. It filters the events to only include those that
are both a call to action and a standard plan card. It then maps the event to get the standard card
element, filters out any falsy values, and finally maps the event to get custom properties for a
paid plan call to action button click. */
const callToActionPlanCardClick$ = callToActionStandardCardClick$.pipe(
  filter((standardCard) => isPlanStandardCard(standardCard.dataset)),
  map(getLeadButtonClickedCallToActionPaidPlanCustomProperties)
);

merge(
  callToActionCommunityCardClick$,
  callToActionSpecCardClick$,
  callToActionPlanCardClick$).subscribe(triggerLeadButtonClickedEvent);

/* Observable stream is set up to track when a user clicks on
  the favorite icon within a standard card element in the listing for you section and triggers the
  `triggerListingClick` function with the appropriate custom properties. */
const favoriteIconClick$ = listingForYouSectionClick$.pipe(
  filter((event) => isFavoriteIcon(event)),
  map((event) => ({
    interactionType: "favorite",
    card: getStandardCardElement(event),
  }))
);

/* Observable stream that listens for click events within the listing for you section.
   It filters the events to only include those where the clicked element is a linkable
   listing element (determined by the `isLinkableListingElement`
  function). */
const linkableListingElementClick$ = listingForYouSectionClick$.pipe(
  filter((event) => isLinkableListingElement(event)),
  map((event) => ({
    interactionType: "link_click",
    card: getStandardCardElement(event),
  }))
);

/* The `cardClick$` constant is creating an observable stream that listens for click events within
  the listing for you section. It filters the events to only include those where the clicked element
  is a standard card element (determined by the `isStandardCard` function). */
const cardClick$ = listingForYouSectionClick$.pipe(
  filter((event) => isStandardCard(event)),
  map((event) => ({
    interactionType: "card_click",
    card: getStandardCardElement(event),
  }))
);

/* Observable stream `listingForYouSectionReady$` that listens for
  a custom event named 'listingForYouSectionReady' triggered on the `document.body`. Once this
  custom event is detected, the subsequent `subscribe` function is called. Within the `subscribe`
  function, it sets up subscriptions for different observable streams (`favoriteIconClick$`,
  `linkableListingElementClick$`, `cardClick$`) to trigger the
  `triggerListingClick` function when specific conditions are met. */
merge(
  favoriteIconClick$,
  linkableListingElementClick$,
  cardClick$
).subscribe(triggerListingClick);

/* Observable stream `metroSpecificSectionReady$` that listens for a custom event
   named 'metroSpecificSectionReady' triggered on the `document.body`. */
onMetroSpecificArticlesClick$.subscribe(triggerArticlesClick);

// Observers to ModalSubmitted Event Logic
const modalSubmittedClick$ = store$.pipe(
  filter(selectFetchingModal),
  map(selectModal),
  filter(Boolean),
  filter((modalInformation) => modalInformation.name === 'leadGenerator'),
);

const modalSubmittedSpecClick$ = modalSubmittedClick$.pipe(
  filter((modalInformation) => isSpecStandardCard(modalInformation.listingInformation)),
  map(triggerPaidSpecModalSubmitted)
);

const modalSubmittedPlanClick$ = modalSubmittedClick$.pipe(
  filter((modalInformation) => isPlanStandardCard(modalInformation.listingInformation)),
  map(triggerPaidPlanModalSubmitted)
);

const modalSubmittedCommunityClick$ = modalSubmittedClick$.pipe(
  filter((modalInformation) => isCommunityStandardCard(modalInformation.listingInformation)),
  map(triggerPaidCommunityModalSubmitted)
);

merge(
  modalSubmittedSpecClick$,modalSubmittedPlanClick$,modalSubmittedCommunityClick$
).subscribe((customProperties) => {
  triggerModalSubmittedEvent(customProperties)
  triggerIdentifyModalSubmission()
});
